import React, { FormEvent, useState } from 'react';
import PopoverTemplate from '@nkr/components/popover-template';
import { IChainStartItem } from './models';
import { ClusterStatus } from '@nkr/enums/index';
import { SsdTableWrapper } from '@nkr/views/swap-management/swaps/swap-list/positions/styles';
import { IDonor } from '@nkr/models/donor';
import { IMatchRunScenario, IPosition } from '@nkr/models/index';
import { FormControl, OverlayTrigger } from 'react-bootstrap';
import { IListItem } from '@nkr/models/list-item';
import { FormControlWrapper } from '@nkr/views/swap-management/swaps/styles';
import { toast } from 'react-toastify';
import { OfferNowType } from '@nkr/enums/swap-offer-now-type';

interface IProps {
    index: number,
    item: IChainStartItem,
    clusterStatusList: IListItem[],
    scenario: IMatchRunScenario | { stalled: false; },
    onClusterStatusChange: (clusterId: number, clusterStatus: number) => void,
    onCommentChange: (clusterId: number, comment: string) => void,
    onSelected: (id: number) => void
}

const renderChainStartPopover = (index: number, donor: IDonor) => {
    const donorCenterId = donor.rdnCenterId > 0 ? donor.rdnCenterId : donor.center.id;

    return (
        <OverlayTrigger placement='top' overlay={renderDonorInformation(index, donor)}>
            <a href={`/center/${donorCenterId}/donor/${donor.id}/profile/medical-details`} target='_blank'>{donor.alias}</a>
        </OverlayTrigger>
    );
}

const renderDonorInformation = (index: number, donor: IDonor) => {
    return (
        <PopoverTemplate width='1000px' dismiss={false}>
            <SsdTableWrapper condensed striped bordered>
                <thead>
                    <tr>
                        <th>Min</th>
                        <th>Max</th>
                        <th>FAnat</th>
                        <th>CC</th>
                        <th>EBV</th>
                        <th>CMV</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{donor.minWeeksUntilTransplant}</td>
                        <td>{donor.maxWeeksUntilTransplant}</td>
                        <td>{getWhichKidney(donor)}</td>
                        <td>{donor.creatinineClearance}</td>
                        <td>{donor.ebv}</td>
                        <td>{donor.cmv}</td>
                    </tr>
                </tbody>
            </SsdTableWrapper>
        </PopoverTemplate>
    );
}

const getWhichKidney = (donor: IDonor) => {
    if (donor.whichKidney != '' && donor.whichKidney != 'DNK') {
        let results = `${donor.whichKidneyAbbreviated}${donor.numberOfArteries}`;

        if (donor.whichKidney == 'L-opt') {
            results += '(R)';
        }

        if (donor.whichKidney == 'R-opt') {
            results += '(L)';
        }

        return results;
    }

    return '--';
};

const ChainStartRow = (props: IProps) => {
    const [comment, setComment] = useState({ edit: false, value: props.item.donor.cluster.note });

    const handleClusterStatusChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let status = parseInt(e.currentTarget.value);
        let donor = props.item.donor;

        if (status == ClusterStatus.Stalled && donor.donorType == 'NDD' && props.item.donor.cluster.clusterNumber != 1) {
            toast.error(
                <span style={{ fontSize: 18 }}>The cluster starts with a bridge donor and cannot be moved into the STALLED category</span>, {
                autoClose: false,
                className: 'severe-error'
            });
        } else {
            props.onClusterStatusChange(props.item.donor.cluster.id, status);
        }
    }

    const handleCheckboxChange = () => {
        props.onSelected(props.item.donor.id);
    };

    let rowStyle = { padding: 1, backgroundColor: 'white' };

    if (props.item.donor.cluster.clusterStatusId == ClusterStatus.Offers && props.item.donor.cluster.note.toUpperCase() != 'OFFER') {
        rowStyle = { padding: 1, backgroundColor: '#ccff66' };
    } else {
        if (props.item.donor.cluster.note.toUpperCase() != 'OFFER') {
            rowStyle = { padding: 1, backgroundColor: 'orange' };
        }
    }

    const renderDate = (surgeDate: string) => {
        if (props.item.donor.cluster.positions.length == 0) {
            return null;
        }

        let position = props.item.donor.cluster.positions.find((p: IPosition) => p.positionNumber == 1);

        if (!position) return <span>-</span>;

        let highlightStyle = {};

        if (position.offerNowType == OfferNowType.Yellow) {
            highlightStyle = {
                backgroundColor: 'yellow',
                color: 'black',
                fontWeight: 700,
                borderRadius: '2px'
            };
        }

        else if (position.offerNowType == OfferNowType.Purple) {
            highlightStyle = {
                backgroundColor: ' #adadeb',
                color: 'black',
                fontWeight: 700,
                borderRadius: '2px'
            };
        }

        else if (position.offerNowType == OfferNowType.Green) {
            highlightStyle = {
                backgroundColor: 'green',
                color: 'white',
                fontWeight: 700,
                borderRadius: '2px'
            };
        }

        return (
            <span style={highlightStyle}>
                &nbsp;{surgeDate}&nbsp;
            </span>
        );
    };

    const renderNote = () => {
        if (!comment.edit && props.item.donor.cluster.note == "")
            return (
                <div style={{ color: "#ddd" }}>[ click here to add notes... ]</div>
            );

        if (!comment.edit)
            return (
                <div style={{ width: "100%", whiteSpace: "nowrap", textOverflow: "ellipsis" }} title={props.item.donor.cluster.note}>{props.item.donor.cluster.note}</div>
            );

        if (comment.edit)
            return (
                <FormControl
                    value={comment.value}
                    style={{ height: 30, paddingLeft: 6 }}
                    placeholder="Notes pertaining to this swap..."
                    onChange={handleCommentChange}
                    onBlur={handleSaveComment}
                    onKeyUp={handleCancelComment}
                    autoFocus={true}
                />
            );
    }

    const handleEditComment = () => {
        setComment({ edit: true, value: props.item.donor.cluster.note });
    }

    const handleCommentChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        setComment({ ...comment, value: e.currentTarget.value });
    }

    const handleSaveComment = () => {
        setComment({ ...comment, edit: false });

        if (comment.value != props.item.donor.cluster.note)
            props.onCommentChange(props.item.donor.cluster.id, comment.value);
    }

    const handleCancelComment = (e: React.KeyboardEvent<FormControl>) => {
        if (e.key == "Escape") {
            setComment({ ...comment, edit: false });
        }
    }

    const donorCenter = props.item.donor.center.alias;
    const donorCenterId = props.item.donor.rdnCenterId > 0 ? props.item.donor.rdnCenterId : props.item.donor.center.id;

    return (
        <tr style={rowStyle}>
            <td>{props.index + 1}</td>
            <td>{props.item.donor.cluster.chainId}/{props.item.donor.cluster.clusterNumber}</td>
            <td>{props.item.donor.donationTypeAbbreviation}</td>
            <td><a href={'./center/' + donorCenterId + '/profile/edit'} className='btn btn-link' style={{ padding: 0 }} target='_blank'>{donorCenter}</a></td>
            <td>{renderChainStartPopover(props.index + 1, props.item.donor)}</td>
            <td>{props.item.donor.age}</td>
            <td>{props.item.donor.bloodTypeDisplay}</td>
            <td>
                <FormControl
                    componentClass='select'
                    value={props.item.donor.cluster.clusterStatusId}
                    style={{ width: '100%', minWidth: 0 }}
                    bsSize='sm'
                    onChange={handleClusterStatusChange}
                >
                    {props.clusterStatusList
                        .filter(x => x.id == ClusterStatus.Offers || x.id == ClusterStatus.Stalled)
                        .map((status, index) =>
                            <option key={index} value={status.id}>{status.description}</option>
                    )}
                </FormControl>
            </td>
            <td>{renderDate(props.item.donor.earliestSurgeryDateTime)}</td>
            <td>{renderDate(props.item.donor.latestSurgeryDateTime)}</td>
            <td>{props.item.donor.minWeeksUntilTransplant}</td>
            <td>{props.item.donor.maxWeeksUntilTransplant}</td>
            <td style={{ fontWeight: 700 }}>{props.item.donor.donorStrength}</td>
            <td onClick={handleEditComment} title={props.item.donor.cluster.note}>
                {renderNote()}
            </td>
            <td><input type='checkbox' value={props.item.donor.id} checked={props.item.selected} onChange={handleCheckboxChange} /></td>
        </tr>
    );
}

export default ChainStartRow;